/*
 *
 * ShopPickerPopup
 *
 * @version 1.0.0
 * @author Marco Alessandroni
 * @since 05-May-2025
 * -----------------------------------------------
 * COMPONENT PURPOSE:
 * This component renders a full-screen popup to allow users
 * to filter and select points of sale (shops) grouped by tabs.
 * The selection is stored locally inside the popup until the user
 * confirms it by clicking "Applica selezione".
 *
 * MAIN FEATURES:
 * - Manages local temporary selection state (independent from Redux)
 * - Allows per-tab selection via ShopSelector
 * - Tracks tab switching and resets selections if needed
 * - Syncs with Redux only when "Applica selezione" is clicked
 * - Ensures accessibility and mobile layout responsiveness
 *
 * USED IN:
 * - Main layout (via PageTemplate or similar wrapper)
 * -----------------------------------------------
 */

import React, { useState, useEffect, useRef, useMemo, useContext } from 'react'
import PropTypes from 'prop-types'
import Toggle from 'Hoc/ToggleComponent'
import { useMobileDetect } from 'Hoc/ListenerProvider'
import Dialog from '@mui/material/Dialog'
import FlowTemplate from 'Components/Template/FlowTemplate'
// import PortalLink from '@nexidigital/portallink'
import PortalTabs from '@nexidigital/portaltabs'
import PortalButton from '@nexidigital/portalbutton'
import { Dismiss } from '@nexidigital/iconlibrary'
import ShopSelector from 'Components/ShopSelector'
import GroupsInfoPopup from 'Components/GroupsInfoPopup'
import formattedMessage from './languageModule'
import { AnalyticsContext } from 'Hoc/AnalyticsProvider'
import { mergeSelectionWithParents, normalizeShopSelectionPayload } from 'Utility/shopListNormalizer'
import './ShopPickerPopup.scss'

const autoInitializeSmallGroupsPages = new Set(['home', 'transactions', 'results', 'documents'])

const ShopPickerPopup = (props) => {
  const { isSmallScreen } = useMobileDetect()
  const { setPopupClickAnalytics } = useContext(AnalyticsContext)

  // Local selections made in the popup. These will overwrite Redux only when "Applica" is clicked.
  const [localSelections, setLocalSelections] = useState({})

  // Prevent UI from rendering until initial local state is ready.
  const [isInitialized, setIsInitialized] = useState(false)
  // Tracks currently active tab.
  const [selectedTab, setSelectedTab] = useState(() => {
    // Find the index of the first group with selected: true
    if (props.localSelectedTab !== null) {
      return props.localSelectedTab
    }

    const foundIndex = props.selectorGroups.findIndex(group => group.selected === true)
    // If localSelectedTab is null and no group is selected, default to 0
    return foundIndex !== -1 ? foundIndex : 0
  })

  // Needed to compare old tab vs new tab when switching.
  const previousTabRef = useRef(0)

  // This effect runs when the popup opens.
  const [isGroupInfoDialogOpen, setIsGroupInfoDialogOpen] = useState(false)

  const [dirtyTabs, setDirtyTabs] = useState({})

  const prevIndex = previousTabRef.current
  const tabSelected = props.selectorGroups[prevIndex]?.group

  const groupLabelMapper = (group) => {
    const map = {
      OTHER: formattedMessage.onlyPosTerminals
      // add more mappings as needed
    }

    return map[group] || group.replace(/_/g, ' ')
  }

  const labelToGroupMapper = useMemo(() => {
    const reverseMap = {
      [formattedMessage.onlyPosTerminals]: 'OTHER'
      // aggiungi altri se necessario
    }

    props.selectorGroups.forEach(({ group }) => {
      const label = groupLabelMapper(group)
      if (!reverseMap[label]) reverseMap[label] = group
    })

    return reverseMap
  }, [props.selectorGroups])

  // Create a map from normalized group to original group for API calls
  const normalizedToOriginalGroupMap = useMemo(() => {
    const map = {
      NEXI: 'NEXI',
      'INTESA SANPAOLO': 'INTESA_SANPAOLO',
      'NEXI DIRECT': 'NEXI_DIRECT',
      OTHER: 'OTHER'
    }

    return map
  }, [])

  // Costruisci la struttura mergedGroups
  const mergedGroups = useMemo(() => props.selectorGroups.map(group => ({
    ...group,
    shopsList: mergeSelectionWithParents(
      group.shopsList,
      localSelections[group.group] || []
    )
  })), [props.selectorGroups, localSelections])

  useEffect(() => {
    setPopupClickAnalytics && setPopupClickAnalytics(
      formattedMessage.shopPickerSelector,
      formattedMessage.chooseShopLabel,
      formattedMessage.applySelection,
      null,
      formattedMessage.closeLabel,
      false
    )
  }, [])

  useEffect(
    () => () => {
      props.actions.doSetLocalSelectedTab(null)
    },
    []
  )

  useEffect(() => {
    if (props.selectorGroups && props.selectorGroups.length > 0) {
      let selectedIndex

      if (props.localSelectedTab !== null) {
        selectedIndex = props.localSelectedTab
      } else {
        const foundIndex = props.selectorGroups.findIndex(group => group.selected === true)
        selectedIndex = foundIndex !== -1 ? foundIndex : 0
      }

      if (selectedIndex !== -1) {
        setSelectedTab(selectedIndex)
        previousTabRef.current = selectedIndex // Also update the ref to avoid unwanted tab resets
      } else {
        setSelectedTab(0) // Default fallback
      }
    }
  }, [props.selectorGroups])

  /**
   * This effect tracks when the user changes tab.
   * If they switch away from a tab where selections were made but not saved,
   * and nothing was previously applied, it will clear that tab's selections.
   */
  useEffect(() => {
    const prevIndex = previousTabRef.current
    const nextIndex = selectedTab

    previousTabRef.current = nextIndex // 🔁 PRIMA DI TUTTO!

    if (prevIndex !== nextIndex) {
      const prevGroup = props.selectorGroups[prevIndex]?.group

      if (dirtyTabs[prevGroup]) {
        const hasValidSelections = localSelections?.[prevGroup]?.some(
          entry => entry.parent?.isSelected || (entry.shops && entry.shops.some(s => s.isSelected))
        )

        if (!hasValidSelections) {
          setLocalSelections(prev => ({
            ...prev,
            [prevGroup]: [] // solo se è davvero inutile
          }))
        }

        setDirtyTabs(prev => ({
          ...prev,
          [prevGroup]: false
        }))
      }
    }
  }, [selectedTab, props.selectorGroups, dirtyTabs, localSelections])

  /**
   * Listen for the global "tabSwitching" event sent by the PortalTabs component.
   * This lets us sync the selected tab index to trigger tab-dependent behavior.
   */
  useEffect(() => {
    if (props.open) {
      setDirtyTabs({})
      setIsInitialized(true)
    }
  }, [props.open, props.shopSelections])

  // Listens to the global tab change event triggered by PortalTabs
  // and maps it to internal state to track active tab index.
  useEffect(() => {
    const handleTabChange = (event) => {
      const label = event.detail?.label
      const group = labelToGroupMapper[label]
      const originalGroup = normalizedToOriginalGroupMap[group] || group

      if (!group) {
        return
      }

      const index = props.selectorGroups.findIndex(t => t.group === group)
      props.actions.doSetLocalSelectedTab(index)
      // Blocca se il tab è già quello selezionato
      if (index === selectedTab) {
        return
      }
      if (index !== -1) {
        const prevGroup = props.selectorGroups[previousTabRef.current]?.group

        if (prevGroup && prevGroup !== group) {
          // Get original group with underscores for API calls
          setLocalSelections(prev => ({
            ...prev,
            [prevGroup]: []
          }))
          props.actions.clearShopSelection(prevGroup, [])
          props.actions.doClearShopLocalSelection({ group: prevGroup })
          // Reset search filter when switching tabs
          if (props.searchTerm !== '') {
            props.actions.clearSearchTerm()
            props.actions.doGetShopList({ filter: '', currentGroup: originalGroup })
          }
        }

        previousTabRef.current = index
        setSelectedTab(index)
      }
    }

    window.addEventListener('tabSwitching', handleTabChange)
    return () => {
      window.removeEventListener('tabSwitching', handleTabChange)
    }
  }, [props.selectorGroups])

  /**
   * Triggered by the ShopSelector component every time the user toggles checkboxes.
   * It updates the local temporary state for a specific tab/group.
   */
  const handleOnChange = ({ group, updated }) => {
    const enriched = updated.map(entry => ({
      parent: {
        ...entry.parent,
        isSelected: entry.parent?.isSelected === true // forza boolean
      },
      shops: (entry.shops || []).map(shop => ({
        ...shop,
        isSelected: shop?.isSelected === true // forza boolean
      }))
    }))

    setLocalSelections(prev => ({
      ...prev,
      [group]: enriched
    }))

    setDirtyTabs(prev => ({
      ...prev,
      [group]: true
    }))
  }

  /**
   * When the user clicks "Applica selezione":
   * - Filters only the selected shops
   * - Normalizes the structure for Redux
   * - Closes the popup
   */

  const handleApply = () => {
    // Aggrega tutte le selezioni di tutti i gruppi dalla selezione locale
    const activeGroup = props.selectorGroups[selectedTab]?.group

    const allSelections = Object.values(localSelections).flatMap(groupEntries =>
      groupEntries
        .filter(parent => {
          const hasChildSelected = parent.shops && parent.shops.some(shop => shop.isSelected)
          const isParentWithoutChildrenSelected = (!parent.shops || parent.shops.length === 0) && parent.parent?.isSelected
          return hasChildSelected || isParentWithoutChildrenSelected
        })
        .map(parent => ({
          parent: parent.parent,
          shops: (parent.shops || []).filter(shop => shop.isSelected),
          allShopCount: parent.shops?.length ?? 0
        }))
    )

    const { shopIds } = normalizeShopSelectionPayload({
      group: activeGroup,
      data: allSelections,
      selectorGroups: props.selectorGroups
    })

    if (shopIds.length === 0) return

    // Invia una sola volta tutte le selezioni
    props.actions.setShopSelection(activeGroup, allSelections, props.selectorGroups)

    // Pulisci lo stato locale e chiudi il popup
    props.selectorGroups.forEach(groupObj => {
      props.actions.clearSearchTerm({ group: groupObj.group })
      props.actions.doClearShopLocalSelection({ group: groupObj.group })
    })

    setPopupClickAnalytics && setPopupClickAnalytics(
      formattedMessage.shopPickerSelector,
      formattedMessage.chooseShopLabel,
      formattedMessage.applySelection,
      null,
      formattedMessage.closeLabel,
      formattedMessage.applySelection
    )
    props.actions.doClosePopup?.('SHOP_PICKER')
  }

  const handleClose = () => {
    // Reset della selezione locale non applicata per il gruppo attivo
    const activeGroup = props.selectorGroups[selectedTab]?.group
    if (activeGroup) {
      props.actions.doClearShopLocalSelection({ group: activeGroup })
    }

    props.actions.doClosePopup?.('SHOP_PICKER')
    props.actions.clearSearchTerm()
    props.actions.doSetShopSelectionMode('checkbox', 'default')
    setPopupClickAnalytics && setPopupClickAnalytics(
      formattedMessage.shopPickerSelector,
      formattedMessage.chooseShopLabel,
      formattedMessage.applySelection,
      null,
      formattedMessage.closeLabel,
      formattedMessage.closeLabel
    )
  }

  /**
   * Used to disable the "Applica selezione" button unless at least one checkbox is checked.
   */
  const normalizeSelections = (selections) => Object.fromEntries(
    Object.entries(selections).map(([group, entries]) => [
      group,
      entries
        .filter(entry => entry.shops?.some(c => c.isSelected) || entry.parent?.isSelected)
        .map(entry => ({
          parentCode: entry.parent?.code,
          parentSelected: entry.parent?.isSelected,
          selectedChildren: (entry.shops || []).filter(c => c.isSelected).map(c => c.code).sort()
        }))
    ])
  )

  const clearOtherGroups = (activeGroup) => {
    const allGroups = props.selectorGroups.map(g => g.group)
    const groupsToClear = allGroups.filter(g => g !== activeGroup)

    setLocalSelections(prev => {
      const cleared = { ...prev }
      groupsToClear.forEach(group => {
        props.actions.clearShopSelection(group, [])
        cleared[group] = []
      })
      return cleared
    })
  }

  const canApply = useMemo(() => {
    const normalizedCurrent = normalizeSelections(localSelections)
    const normalizedOriginal = normalizeSelections(props.shopSelections)

    const isSelectionDifferent = JSON.stringify(normalizedCurrent) !== JSON.stringify(normalizedOriginal)

    const hasAnySelection = Object.values(normalizedCurrent).some(group =>
      group.some(entry =>
        entry.parentSelected || entry.selectedChildren.length > 0
      )
    )

    return hasAnySelection && isSelectionDifferent
  }, [localSelections, props.shopSelections])

  // Avoid rendering while initial state is being set.
  if (!isInitialized) return null

  /**
   * Create tab content for each group (e.g. NEXI, INTESA, etc.)
   * Each tab renders a ShopSelector with its own data slice.
   */
  const tabData = props.selectorGroups.map(({ group, shopsList }) => {
    const label = groupLabelMapper(group)
    const selections = localSelections[group] || []
    // Prendi la lista mergeata per questo gruppo
    const mergedGroup = mergedGroups.find(g => g.group === group)
    const mergedParents = mergedGroup?.shopsList || []
    const autoInitializeSmallGroups = autoInitializeSmallGroupsPages.has(props.shopSelectionFromPage)

    // Conta gli shop selezionati negli ALTRI gruppi (per il limite max cross-gruppo)
    const externalSelectedCount = Object.entries(localSelections).reduce((acc, [g, entries]) => {
      if (g === group || !Array.isArray(entries)) return acc
      return acc + entries.reduce((sum, entry) => {
        const childSelected = (entry.shops || []).filter(s => s.isSelected).length
        if (childSelected > 0) return sum + childSelected
        return sum + (entry.parent?.isSelected ? 1 : 0)
      }, 0)
    }, 0)

    const content = (
      <div className='shopPickerShopSelection'>
        <section className='shopSelectorRow'>
          <div className='shopSelectorBox'>
            <ShopSelector
              key={`${group}-${props.open}`}
              data={{
                group,
                initialParents: mergedParents,
                selections,
                maxSelectableShops: props.maxSelectableShops,
                externalSelectedCount
              }}
              autoInitializeSmallGroups={autoInitializeSmallGroups}
              onChange={handleOnChange}
              tabSelected={tabSelected}
              clearAction={clearOtherGroups}
              shopListLoading={props.shopListLoading}
              maxSelectableShops={props.maxSelectableShops}
            />
          </div>
        </section>
      </div>
    )

    return { label, content }
  })

  return (
    <>
      <Toggle>
        <Dialog fullScreen open={props.open} className='shopPickerDialog'>
          <FlowTemplate
            flowBackground
            titleNavigation={formattedMessage.shopPickerSelector}
            iconCloseRight={<Dismiss size={isSmallScreen ? '24' : '20'} type='line' />}
            labelButtonRightNav={formattedMessage.closeLabel}
            onClickClose={handleClose}
            emptyPage
          >
            <section className='shopPickerPopup'>
              <div className='shopPickerContainer'>
                <div className='shopPickerPopupHeader'>
                  <h1 className='shopPickerPopupTitle'>
                    {formattedMessage.chooseShopLabel}
                    {props.selectorGroups.length > 1 && (
                      <>
                        <PortalButton
                          textButton
                          size='small'
                          label={formattedMessage.whatGroupsAreInfo}
                          onClick={() => setIsGroupInfoDialogOpen(true)}
                        />
                        {isGroupInfoDialogOpen && (
                          <GroupsInfoPopup
                            onClose={() => setIsGroupInfoDialogOpen(false)}
                            availableGroups={props.selectorGroups.map(group => group.group)}
                          />
                        )}
                      </>
                    )}
                  </h1>
                  <div className={`shopPickerPopupContent ${tabData.length > 1 ? 'hasTabs' : ''}`}>
                    {tabData.length > 1
                      ? (
                        <PortalTabs merchant tabData={tabData} preSelectedTab={selectedTab} />
                        )
                      : (
                          tabData[0]?.content
                        )}
                  </div>
                </div>
              </div>
              <div className='shopPickerPopupFooter'>
                <PortalButton label={formattedMessage.applySelection} onClick={handleApply} disabled={!canApply} />
              </div>
            </section>
          </FlowTemplate>
        </Dialog>
      </Toggle>
    </>
  )
}

ShopPickerPopup.propTypes = {
  open: PropTypes.bool.isRequired,
  selectorGroups: PropTypes.array.isRequired,
  shopSelections: PropTypes.object.isRequired,
  actions: PropTypes.object,
  shopListLoading: PropTypes.bool,
  shopSelectionFromPage: PropTypes.string,
  shopLocalSelection: PropTypes.object,
  searchTerm: PropTypes.string,
  localSelectedTab: PropTypes.number,
  maxSelectableShops: PropTypes.number
}

ShopPickerPopup.defaultProps = {
  // Add here some default propTypes values
  actions: {},
  shopListLoading: false,
  shopSelectionFromPage: 'default', // Default to 'default' if not specified,
  shopLocalSelection: {},
  searchTerm: '',
  localSelectedTab: null
}

export default ShopPickerPopup
